
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { StatCard } from "@/components/StatCard";
import { CalendarRange } from "lucide-react";
import { usePerformanceData } from "@/hooks/usePerformanceData";

interface RewardsPrognosisProps {
  address: string;
}

export function RewardsPrognosis({ address }: RewardsPrognosisProps) {
  const { data: performanceData, isLoading } = usePerformanceData(address);
  
  // Fetch market data for USD conversion
  const { data: marketData } = useQuery({
    queryKey: ['marketData'],
    queryFn: async () => {
      const { data } = await supabase
        .from('dusk_market_data')
        .select('current_price_usd')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();
      return data;
    },
  });

  if (isLoading) {
    return <div>Loading prognosis...</div>;
  }

  if (!performanceData) {
    return null;
  }

  // Use 24hr average for weekly projection
  const weeklyProjection = performanceData.last24hr.dailyAverage * 7;
  
  // Use 7-day daily average for monthly projection
  const monthlyProjection = performanceData.last7days.dailyAverage * 30;
  
  // Use monthly daily average for yearly projection
  const yearlyProjection = performanceData.lastMonth.dailyAverage * 365;

  // Calculate USD values
  const duskPrice = marketData?.current_price_usd || 0;
  const weeklyUsdValue = weeklyProjection * duskPrice;
  const monthlyUsdValue = monthlyProjection * duskPrice;
  const yearlyUsdValue = yearlyProjection * duskPrice;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Rewards Prognosis</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            title="Weekly Projection"
            value={`${Math.floor(weeklyProjection)} DUSK`}
            description="Based on 24h performance"
            icon={<CalendarRange className="h-4 w-4" />}
            tooltipContent="Projected rewards for the next 7 days based on the last 24 hours performance"
            usdValue={weeklyUsdValue}
          />
          <StatCard
            title="Monthly Projection"
            value={`${Math.floor(monthlyProjection)} DUSK`}
            description="Based on 7-day average"
            icon={<CalendarRange className="h-4 w-4" />}
            tooltipContent="Projected rewards for the next 30 days based on the last 7 days average performance"
            usdValue={monthlyUsdValue}
          />
          <StatCard
            title="Yearly Projection"
            value={`${Math.floor(yearlyProjection)} DUSK`}
            description="Based on monthly average"
            icon={<CalendarRange className="h-4 w-4" />}
            tooltipContent="Projected rewards for the next 365 days based on the last month's average performance"
            usdValue={yearlyUsdValue}
          />
        </div>
      </CardContent>
    </Card>
  );
}
